import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { WashDto, WashGetPaginatedDto } from "@/core/api/generated/v1";

import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type WashAutocompleteOption = BaseAutocompleteOption<WashDto>;

export const getWashOptionTitle = (data: WashDto): string =>
  StringHelper.join(
    [
      data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber,
      data?.stageHistory && enumService.getEnumValueName("WashStage", data?.stageHistory?.stage),
    ],
    " | ",
    {
      skipEmpty: true,
    },
  );

export const washToOption = (data: WashDto): WashAutocompleteOption => ({
  id: data.id!,
  title: getWashOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface WashAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<WashDto> {
  searchFilters?: Partial<Omit<WashGetPaginatedDto, "search" | "includeIds">>;
}

export default function WashAutocomplete({
  searchFilters,
  isPreload = true,
  ...otherProps
}: WashAutocompleteProps) {
  console.log("searchFilters", searchFilters);
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={washToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.washesApi.apiV1WashesGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          washGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          washGetPaginatedDto: {
            ...params.washGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Wash'
      placeholder='Search...'
      renderOption={(props, option: WashAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='wash' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
